/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';

// Generic single-select-per-group chip row (the Services card's type /
// lifecycle facets). Same visual language as PrModeChips, different
// semantics: every group is optional (nothing selected = no filter) and a
// second click on the active chip clears it.

export type FilterChipGroup = {
  key: string;
  items: { key: string; label: string; count: number }[];
  value: string | null;
  onChange: (next: string | null) => void;
};

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    background: 'transparent',
    color: theme.palette.text.secondary,
    fontFamily: 'inherit',
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: 1,
    padding: theme.spacing(0.75, 1.5),
    cursor: 'pointer',
    transition: 'background-color 120ms cubic-bezier(0.25, 1, 0.5, 1), border-color 120ms cubic-bezier(0.25, 1, 0.5, 1)',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
    '&:hover': {
      borderColor: theme.palette.text.secondary,
      color: theme.palette.text.primary,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1,
    },
  },
  chipActive: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(255, 110, 91, 0.16)'
        : 'rgba(255, 110, 91, 0.10)',
    borderColor: theme.palette.primary.main,
    color: theme.palette.text.primary,
    fontWeight: 600,
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  count: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    fontVariantNumeric: 'tabular-nums',
  },
  countActive: {
    color: theme.palette.primary.light,
  },
  sep: {
    width: 1,
    alignSelf: 'stretch',
    margin: theme.spacing(0.5, 0.5),
    backgroundColor: theme.palette.divider,
  },
}));

export const FilterChips = ({
  groups,
  ariaLabel,
}: {
  groups: FilterChipGroup[];
  ariaLabel: string;
}) => {
  const classes = useStyles();
  const visible = groups.filter(g => g.items.length > 1);
  if (visible.length === 0) return null;

  return (
    <Box role="group" aria-label={ariaLabel} className={classes.row}>
      {visible.map((g, gi) => (
        <React.Fragment key={g.key}>
          {gi > 0 && <span className={classes.sep} aria-hidden />}
          {g.items.map(item => {
            const active = item.key === g.value;
            return (
              <button
                key={item.key}
                type="button"
                aria-pressed={active}
                className={`${classes.chip}${active ? ` ${classes.chipActive}` : ''}`}
                onClick={() => g.onChange(active ? null : item.key)}
              >
                {item.label}
                <Typography
                  component="span"
                  className={`${classes.count}${active ? ` ${classes.countActive}` : ''}`}
                >
                  {item.count > 99 ? '99+' : item.count}
                </Typography>
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </Box>
  );
};
